import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Loader2, Sparkles, LayoutTemplate, Layers, Users, Quote, PlusCircle, Upload, MessageCircle, Megaphone, CalendarClock, CalendarDays, BarChart3, Plug, Settings2, PauseCircle, PlayCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { StatusBadge } from "../admin/AFields";
import StudioCard from "./StudioCard";

const WA_STYLE = {
  "NOT CONFIGURED": "bg-slate-100 text-slate-600",
  "ACTION REQUIRED": "bg-saffron-100 text-saffron-600",
  "CONNECTED": "bg-brandgreen-100 text-brandgreen",
  "ACTIVE": "bg-brandgreen-100 text-brandgreen",
  "ERROR": "bg-red-50 text-red-500",
};

export function StudioHome() {
  const { base } = useOutletContext();
  const [daily, setDaily] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [people, setPeople] = useState([]);
  const [stats, setStats] = useState({});
  const [wa, setWa] = useState(null);
  const [auto, setAuto] = useState(null);
  const [todayOcc, setTodayOcc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/studio/daily").then((r) => setDaily(r.data?.id ? r.data : null)).catch(() => {});
    api.get("/studio/orgs").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/studio/people").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/greetings/list").then((r) => setStats((s) => ({ ...s, greetings: r.data.length, drafts: r.data.filter((g) => g.status === "draft").length }))).catch(() => {});
    api.get("/greetings/contacts").then((r) => setStats((s) => ({ ...s, contacts: r.data.length }))).catch(() => {});
    api.get("/greetings/templates").then((r) => setStats((s) => ({ ...s, templates: r.data.length }))).catch(() => {});
    api.get("/studio/quotes").then((r) => setStats((s) => ({ ...s, quotes: r.data.length }))).catch(() => {});
    api.get("/wa/status").then((r) => setWa(r.data)).catch(() => setWa({ state: "NOT CONFIGURED" }));
    api.get("/wa/automation").then((r) => setAuto(r.data)).catch(() => {});
    api.get("/wa/campaigns?status=scheduled").then((r) => setStats((s) => ({ ...s, scheduled: r.data.length }))).catch(() => {});
    api.get("/greetings/occasions").then((r) => {
      const md = new Date().toISOString().slice(5, 10);
      const hit = r.data.find((o) => o.active !== false && String(o.date || "").slice(5, 10) === md);
      setTodayOcc(hit ? hit.name : "");
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const generateDaily = async () => {
    setBusy(true);
    try {
      const r = await api.post("/studio/daily/generate", { language: "bilingual" });
      toast.success(`Today's draft generated (style: ${r.data.style}) — review and publish`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const togglePause = async () => {
    try {
      const r = await api.post(auto?.paused ? "/wa/automation/resume" : "/wa/automation/pause-all");
      toast.success(r.data.paused ? "ALL AUTOMATION PAUSED — manual work still allowed" : "Automation resumed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const org = orgs.find((o) => o.is_default) || orgs[0] || {};
  const person = people[0] || null;
  const waState = wa?.state || "NOT CONFIGURED";

  const QUICK = [
    ["Create Greeting", `${base}/create`, PlusCircle], ["AI Generator", `${base}/ai`, Sparkles],
    ["Templates", `${base}/templates`, Layers], ["Contacts", `${base}/contacts`, Users],
    ["Import Contacts", `${base}/contacts`, Upload], ["WhatsApp Sender", `${base}/sender`, MessageCircle],
    ["Campaigns", `${base}/campaigns`, Megaphone], ["Scheduled", `${base}/campaigns`, CalendarClock],
    ["Calendar", `${base}/calendar`, CalendarDays], ["Delivery Reports", `${base}/campaigns`, BarChart3],
    ["Integrations", `${base}/integrations`, Plug], ["Settings", `${base}/settings`, Settings2],
  ];

  return (
    <div data-testid="studio-home">
      <SEO title="Greeting Studio" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Greeting Studio</h1>
          <p className="mt-1 text-sm text-slate-500">AI-powered greetings for Sahaj Sarathi Foundation & SCT Legal Associates — preview, approve, send.</p>
        </div>
        <div className="flex items-center gap-2">
          {auto && (
            <button onClick={togglePause} data-testid="pause-all-automation"
              className={`flex items-center gap-2 rounded-md px-4 py-2.5 text-xs font-bold transition-colors ${auto.paused ? "bg-brandgreen-100 text-brandgreen" : "bg-red-50 text-red-600 hover:bg-red-100"}`}>
              {auto.paused ? <PlayCircle size={15} /> : <PauseCircle size={15} />} {auto.paused ? "Resume Automation" : "Pause All Automation"}
            </button>
          )}
          <Link to={`${base}/create`} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="studio-create-btn"><Sparkles size={15} /> Create Greeting</Link>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="status-strip">
        <span className="rounded-full bg-brandgreen-100 px-3.5 py-1.5 text-xs font-bold text-brandgreen" data-testid="status-ai">AI — ACTIVE</span>
        <Link to={`${base}/whatsapp`} className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${WA_STYLE[waState]}`} data-testid="status-whatsapp">WHATSAPP — {waState}</Link>
        <span className="rounded-full bg-navy-50 px-3.5 py-1.5 text-xs font-bold text-navy" data-testid="status-occasion">TODAY: {todayOcc || "No occasion"}</span>
        <span className="rounded-full bg-navy-50 px-3.5 py-1.5 text-xs font-bold text-navy" data-testid="status-drafts">DRAFTS READY: {stats.drafts ?? "…"}</span>
        <Link to={`${base}/campaigns`} className="rounded-full bg-navy-50 px-3.5 py-1.5 text-xs font-bold text-navy" data-testid="status-scheduled">SCHEDULED: {stats.scheduled ?? "…"}</Link>
        {auto?.paused && <span className="rounded-full bg-red-50 px-3.5 py-1.5 text-xs font-bold text-red-600" data-testid="status-paused">AUTOMATION PAUSED</span>}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {QUICK.map(([label, to, Icon]) => (
          <Link key={label} to={to} data-testid={`quick-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-saffron-100 text-saffron-600"><Icon size={17} /></span>
            <span className="text-sm font-bold text-navy">{label}</span>
          </Link>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[["greetings", "Greetings", LayoutTemplate, `${base}/library`], ["contacts", "Contacts", Users, `${base}/contacts`],
          ["templates", "Templates", Layers, `${base}/templates`], ["quotes", "Quotes", Quote, `${base}/quotes`]].map(([k, l, Icon, to]) => (
          <Link key={k} to={to} className="rounded-lg border border-gray-200 bg-white p-5 transition-shadow hover:shadow-md" data-testid={`studio-stat-${k}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{l}</p>
              <Icon size={16} className="text-saffron-600" />
            </div>
            <p className="mt-2 font-display text-3xl font-bold text-navy">{stats[k] ?? "…"}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="studio-daily">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy"><Sun size={19} className="text-saffron-600" /> Daily Good Morning</h2>
            <button onClick={generateDaily} disabled={busy} className="btn-dark !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="daily-generate-btn">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate Today's Draft
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">AI rotates style daily. Auto-prepare runs every morning for upcoming occasions — nothing sends without your approval (Safe Mode default).</p>
          {daily ? (
            <div className="mt-4 rounded-lg bg-navy-50/50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-bold text-navy">{daily.title}</p>
                  <p className="text-xs text-slate-500">{daily.date} · style: {daily.style || "—"}</p>
                </div>
                <StatusBadge value={daily.status} />
              </div>
              <p className="mt-2 line-clamp-3 text-sm text-slate-600">{daily.message}</p>
              <div className="mt-3 flex gap-3">
                <Link to={`${base}/create?id=${daily.id}`} className="text-xs font-bold text-saffron-600 hover:text-navy" data-testid="daily-edit-link">Review & Edit →</Link>
                <Link to={`${base}/sender?greeting_id=${daily.id}`} className="text-xs font-bold text-brandgreen hover:text-navy" data-testid="daily-send-link">Approve & Send →</Link>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-400">No daily greeting yet — generate one.</p>
          )}
        </div>
        <div>
          {daily && <StudioCard greeting={daily} org={org} person={person} style={daily.style || "elegant"} size="1:1" />}
        </div>
      </div>
    </div>
  );
}

export default StudioHome;
